<template>
  <div class="question_container">
    <template v-if="isCreateQuestion">
      <div class="creation__field">
        <div class="field_content">
          <!-- CUSTOM FIELD -->
          <div>
            <div
              class="label_field"
              v-text="`${$t('workflow.status.question.type')}*`"
            />
            <base-autocomplete
              v-model="form.typeQuestion"
              :items="typeItems"
              :placeholder="$t('question.chooseAType')"
              item-text="name"
              item-value="type"
              :error-messages="typeErrors"
              :has-error="typeErrors.length > 0"
              @blur="$v.form.typeQuestion.$touch()"
            />
          </div>
          <!-- SLUG -->
          <div>
            <div class="label_field" v-text="`${$t('utils.dataSlug')}*`" />
            <BaseTextField
              v-model="form.slug"
              :error-messages="slugErrors"
              :has-error="slugErrors.length > 0"
              @blur="$v.form.slug.$touch()"
            />
          </div>
          <!-- NAME -->
          <div>
            <div class="label_field" v-text="`${$t('utils.dataName')}*`" />
            <BaseTextField
              v-model="form.name"
              :error-messages="nameErrors"
              :has-error="nameErrors.length > 0"
              @blur="$v.form.name.$touch()"
              @change="propertyChange('name')"
            />
          </div>
          <!-- LABEL -->
          <div>
            <div
              class="label_field"
              v-text="`${$t('course.questionAsked')}*`"
            />
            <BaseTextField
              v-model="form.label"
              :error-messages="labelErrors"
              :has-error="labelErrors.length > 0"
              @blur="$v.form.label.$touch()"
              @change="propertyChange('label')"
            />
          </div>

          <div class="help_title_section">
            <span class="help_label">{{ $t("utils.settings") }}</span>
            <v-tooltip v-if="isQuestionRequireOptions" top>
              <template #activator="{ on }">
                <button
                  class="bulk-button"
                  v-on="on"
                  @click.stop="isHelpSectionOpen = true"
                >
                  <base-icon
                    :icon="bulkOptionInsert ? '$mdiPlus' : '$bulkInsertIcon'"
                    class="ml-auto"
                    color="primary"
                    @click.stop="bulkOptionInsert = !bulkOptionInsert"
                  />
                </button>
              </template>
              <span
                v-text="$t('course.forms.question.option.createManyOptions')"
              />
            </v-tooltip>
            <div class="separator" />
            <BaseIcon
              :icon="!isHelpSectionOpen ? '$mdiMenuDown' : '$mdiMenuUp'"
              color="primary"
              @click="isHelpSectionOpen = !isHelpSectionOpen"
            />
          </div>
          <div v-if="isHelpSectionOpen" class="input-fields">
            <!-- Extractable -->
            <div
              v-if="['FILE', 'MULTIPLE_FILE'].includes(form.typeQuestion)"
              class="d-flex align-center px-2"
            >
              <v-switch
                v-model="form.canWizidee"
                inset
                @change="propertyChange('canWizidee')"
              />
              <div
                class="label_field"
                v-text="$t('utils.wizideeExtractable')"
              />

              <div class="ml-1"><base-icon icon="$wizideeFile" /></div>
            </div>
            <template v-if="controlOptionOn">
              <div class="option_container">
                <div class="label_field" v-text="$t('utils.regexControl')" />
                <BaseTextField
                  v-model="form.conditions"
                  @change="propertyChange('conditions')"
                />
                <div
                  class="label_field"
                  v-text="$t('utils.regexErrorMessage')"
                />
                <BaseTextField
                  v-model="form.errorMessage"
                  @change="propertyChange('errorMessage')"
                />
              </div>
            </template>
            <!-- OPTION SECTION -->
            <template v-if="isQuestionRequireOptions">
              <div class="input_group">
                <template v-if="bulkOptionInsert">
                  <base-text-area
                    v-model="form.bulkOption"
                    :has-error="bulkOptionError.length > 0"
                    :error-messages="bulkOptionError"
                    :placeholder="
                      $t('course.forms.question.option.bulkOptionFormat')
                    "
                  />
                </template>
                <template v-else>
                  <div
                    v-for="(v, index) in $v.form.options.$each.$iter"
                    :key="index"
                    class="option_item"
                  >
                    <BaseTextField
                      :ref="`option${+index + 1}`"
                      v-model.trim="form.options[index].name"
                      :error-messages="optionErrors[index]"
                      :label="
                        $t('course.forms.question.option.label', {
                          number: +index + 1,
                        })
                      "
                      :has-error="optionErrors[index].length > 0"
                      :placeholder="
                        $t('course.forms.question.option.placeholder', {
                          number: +index + 1,
                        })
                      "
                      clearable
                      class="option_name"
                      @blur="v.name.$touch()"
                    />
                    <div class="option_item_actions">
                      <BaseButtonIcon
                        :disabled="index == 0"
                        color="primary"
                        class="rounded"
                        icon="$mdiArrowUpThick"
                        x-small
                        @click="moveOption(+index, false)"
                      />

                      <BaseButtonIcon
                        :disabled="
                          isOptionDisabled(index, $v.form.options.$each.$iter)
                        "
                        color="primary"
                        class="rounded mx-2"
                        icon="$mdiArrowDownThick"
                        x-small
                        @click="moveOption(+index, true)"
                      />
                      <BaseButtonIcon
                        :disabled="form.options.length < 2"
                        color="secondary"
                        x-small
                        icon="$mdiTrashCanOutline"
                        @click="deleteOption(index)"
                      />
                    </div>
                  </div>
                  <div v-if="hasFreeOption" class="option_item">
                    <BaseTextField
                      :value="$t('utils.other')"
                      :label="
                        $t('course.forms.question.option.label', {
                          number: +form.options.length + 1,
                        })
                      "
                      disabled
                      clearable
                      class="option_name"
                    />
                    <div class="option_item_actions">
                      <BaseButtonIcon
                        disabled
                        color="primary"
                        class="rounded"
                        icon="$mdiArrowUpThick"
                        x-small
                      />
                      <BaseButtonIcon
                        disabled
                        color="primary"
                        class="rounded ml-2"
                        icon="$mdiArrowDownThick"
                        x-small
                      />
                      <BaseButtonIcon
                        color="error"
                        class="ml-2"
                        x-small
                        icon="$mdiTrashCanOutline"
                        @click="hasFreeOption = false"
                      />
                    </div>
                  </div>
                  <div class="d-flex justify-center">
                    <BaseButton
                      icon="$mdiPlus"
                      class="mr-3"
                      @click="addOption()"
                    >
                      {{ $t("course.forms.question.utils.addOption") }}
                    </BaseButton>

                    <BaseButton
                      v-if="
                        (form.typeQuestion === 'RADIOBUTTON' ||
                          form.typeQuestion === 'CHECKBOX') &&
                        !hasFreeOption
                      "
                      icon="$mdiPlus"
                      @click="addOptionOther()"
                    >
                      {{ $t("course.forms.question.utils.addOptionOther") }}
                    </BaseButton>
                  </div>
                </template>
              </div>
            </template>
            <!-- HELP SECTION -->
            <div class="help_section">
              <div class="d-flex align-center mb-4">
                <BaseIcon
                  class="mr-2"
                  medium
                  icon="$mdiInformation"
                  color="#353542"
                />
                <div class="help_title" v-text="$t('utils.help')" />
              </div>
              <BaseTextArea
                v-model="form.helpText"
                rows="3"
                :label="$t('course.forms.question.helpText.label')"
              />
              <BaseFileInput
                :val="form.helpFile"
                truncate-length="50"
                append-icon="$mdiDownload"
                label="Fichier d'aide"
                prepend-icon=""
                :clearable="false"
                outlined
                @change="(value) => (form.helpFile = value)"
              />
            </div>
          </div>
        </div>
        <div class="widget__actions">
          <BaseButton
            class="btn__actions"
            :loading="isSubmiting"
            @click="submit"
          >
            {{ $t("course.createQuestion") }}

            <BaseIcon icon="$mdiPlus" small />
          </BaseButton>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="template_section_select">
        <div class="items">
          <v-text-field
            v-model="questionTemplateFilter.slug"
            outlined
            :label="'Rechercher une question'"
            prepend-inner-icon="$mdiMagnify"
            dense
            hide-details
            @input="filterQuestionTemplates"
          />
          <div class="separator" />
        </div>
        <div class="questions__list" @scroll="handleQuestionTemplateScroll">
          <template
            v-if="
              !questionTemplate.items.length && !questionTemplateFilter.loading
            "
          >
            <div class="text-center">Aucune question</div></template
          >
          <div
            v-for="(tempQuestion, index) in questionTemplate.items"
            :key="`${index}-tmpl`"
            class="questions__item"
            :draggable="true"
            @dragstart="handleDrag($event, tempQuestion)"
          >
            <div class="left_item">
              <div class="question__name">{{ tempQuestion.name }}</div>
              <div class="question__slug">{{ tempQuestion.slug }}</div>
            </div>
            <div class="right_item">{{ tempQuestion.typeQuestion }}</div>
          </div>
          <template v-if="questionTemplateFilter.loading">
            <v-skeleton-loader
              v-for="x in 5"
              :key="x"
              class="item_skeleton"
              type="text"
            />
          </template>
        </div>
      </div>

      <div class="widget__actions">
        <BaseButton
          class="btn__actions"
          @click="isCreateQuestion = !isCreateQuestion"
        >
          {{ $t("course.newQuestion") }}
          <BaseIcon icon="$mdiPlus" small />
        </BaseButton>
      </div>
    </template>
  </div>
</template>

<script>
import {
  createQuestionTemplate,
  fetchQuestionTemplates,
} from "@/core/Services/questionTemplate.service";
import { validationMixin } from "vuelidate";
import { maxLength, minLength, required } from "vuelidate/lib/validators";

export default {
  name: "QuestionsTemplateWidget",
  mixins: [validationMixin],
  data() {
    return {
      questionTemplate: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      questionTemplateFilter: {
        loading: false,
        page: 1,
        pageSize: 10,
        slug: undefined,
        searchTimeoutRef: undefined,
      },
      isSubmiting: false,
      isCreateQuestion: false,
      bulkOptionInsert: false,
      typeItems: [
        { name: this.$t("question.types.text"), type: "TEXT" },
        { name: this.$t("question.types.textarea"), type: "TEXTAREA" },
        { name: this.$t("question.types.number"), type: "NUMBER" },
        { name: this.$t("question.types.date"), type: "DATE" },
        { name: this.$t("question.types.datetime"), type: "DATETIME" },
        { name: this.$t("question.types.select"), type: "SELECT" },
        { name: this.$t("question.types.checkbox"), type: "CHECKBOX" },
        { name: this.$t("question.types.radiobutton"), type: "RADIOBUTTON" },
        { name: this.$t("question.types.file"), type: "FILE" },
        {
          name: this.$t("question.types.multiple_file"),
          type: "MULTIPLE_FILE",
        },
        { name: this.$t("question.types.signature"), type: "SIGNATURE" },
        { name: this.$t("question.types.datagrid"), type: "DATAGRID" },
        { name: this.$t("question.types.row_datagrid"), type: "ROW_DATAGRID" },
      ],
      form: {
        typeQuestion: "",
        label: "",
        name: "",
        slug: "",
        helpText: "",
        helpFile: null,
        options: [],
        deletedOptionIds: [],
        canWizidee: false,
        conditions: "",
        errorMessage: "",
        bulkOption: "",
      },
      hasFreeOption: false,
      updatedProps: [],
      isHelpSectionOpen: true,
    };
  },
  validations() {
    let validations = {};
    validations.form = {
      name: {
        required,
        maxLength: maxLength(255),
      },
      slug: {
        required,
        maxLength: maxLength(255),
        notSpecialChar: (value) => {
          return value ? /^[a-zA-Z0-9_-]+$/.test(value) : true;
        },
      },
      label: {
        required,
        maxLength: maxLength(255),
      },
      typeQuestion: {
        required,
      },
    };
    if (this.isQuestionRequireOptions) {
      if (this.bulkOptionInsert) {
        validations.form = {
          ...validations.form,
          bulkOption: {
            required,
          },
        };
      } else {
        validations.form = {
          ...validations.form,
          options: {
            minLength: minLength(1),
            $each: {
              name: {
                required,
                maxLength: maxLength(255),
              },
            },
          },
        };
      }
    }
    return validations;
  },
  computed: {
    controlOptionOn() {
      return ["NUMBER", "TEXT", "TEXTAREA"].includes(this.form.typeQuestion);
    },

    isSlugEditionDisabled() {
      return this.questionTemplate.id;
    },
    isQuestionRequireOptions() {
      const requireOptionsRegex =
        /^SELECT|RADIOBUTTON|CHECKBOX|DATAGRID|ROW_DATAGRID$/;
      return (
        this.form.typeQuestion &&
        requireOptionsRegex.test(this.form.typeQuestion)
      );
    },
    typeErrors() {
      const errors = [];
      if (!this.$v.form.typeQuestion.$dirty) return errors;
      if (!this.$v.form.typeQuestion.required)
        errors.push(this.$t("course.forms.question.typeQuestion.required"));
      return errors;
    },
    slugErrors() {
      const errors = [];
      if (!this.$v.form.slug.$dirty) return errors;
      if (!this.$v.form.slug.required)
        errors.push(this.$t("utils.fieldIsRequired"));
      if (!this.$v.form.slug.maxLength) {
        errors.push(
          this.$t("course.forms.question.slug.maxLength", { n: 255 })
        );
      }
      if (!this.$v.form.slug.notSpecialChar) {
        errors.push(
          this.$t("course.forms.question.slug.specialCharError", { n: 255 })
        );
      }
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      if (!this.$v.form.name.required) {
        errors.push(this.$t("course.forms.question.name.required"));
      }
      if (!this.$v.form.name.maxLength) {
        errors.push(
          this.$t("course.forms.question.name.maxLength", { n: 255 })
        );
      }
      return errors;
    },
    labelErrors() {
      const errors = [];
      if (!this.$v.form.label.$dirty) return errors;
      if (!this.$v.form.label.required) {
        errors.push(this.$t("course.forms.question.label.requiredQuestion"));
      }
      if (!this.$v.form.label.maxLength) {
        errors.push(
          this.$t("course.forms.question.label.maxLengthQuestion", { n: 255 })
        );
      }
      return errors;
    },
    bulkOptionError() {
      const errors = [];
      if (!this.$v.form.bulkOption.$dirty) return errors;
      !this.$v.form.bulkOption.required &&
        errors.push(this.$t("course.forms.question.option.required"));
      return errors;
    },
    optionErrors() {
      const errors = [];
      if (!this.$v.form.options) {
        return errors;
      }
      for (
        let index = 0;
        index < Object.keys(this.$v.form.options.$each.$iter).length;
        index++
      ) {
        errors.push([]);
        const option = this.$v.form.options.$each.$iter[index.toString()];
        if (!option.name.$dirty) {
          continue;
        }
        if (!option.name.maxLength) {
          errors[index] = [
            this.$t("course.forms.question.option.maxLength", { number: 255 }),
          ];
          continue;
        }
        if (!option.name.required) {
          errors[index] = [this.$t("course.forms.question.option.required")];
        }
      }
      return errors;
    },
  },
  watch: {
    isQuestionRequireOptions(newVal) {
      if (newVal && this.form.options.length < 1) this.addOption();
    },
  },
  created() {
    this.fetchQuestionTemplates();
  },
  methods: {
    handleDrag(event, template) {
      event.dataTransfer.setData(
        "application/json",
        JSON.stringify({
          ...template,
          questionType: "TEMPLATE",
        })
      );
    },
    //creation
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.isSubmiting = true;
      await this.createQuestionTemplate();
      this.isSubmiting = false;
      this.reset();
    },
    propertyChange(property) {
      if (!this.updatedProps.includes(property))
        this.updatedProps.push(property);
    },
    buildQuestion() {
      let question = {
        ...this.form,
        hasFreeOption: this.hasFreeOption,
      };
      return question;
    },
    removeHelpSection() {
      this.isHelpSectionOpen = false;
      this.form.helpText = "";
      this.form.helpFile = "";
      this.propertyChange("helpText");
      this.propertyChange("helpFile");
    },
    helpFileChange(value) {
      this.form.helpFile = value;
      this.propertyChange("helpFile");
    },
    addOption() {
      this.form.options.push({ order: this.form.options.length });
      this.orderOptions();
    },
    moveOption(index, upperOrder) {
      let temp = this.form.options[index];
      this.form.options.splice(
        index,
        1,
        this.form.options[upperOrder ? index + 1 : index - 1]
      );
      this.form.options.splice(upperOrder ? index + 1 : index - 1, 1, temp);
      this.orderOptions();
    },
    deleteOption(index) {
      /* saved options in database to delete */
      if (this.form.options[index].id) {
        this.form.deletedOptionIds.push(this.form.options[index].id);
      }
      this.form.options.splice(index, 1);
      this.orderOptions();
    },
    orderOptions() {
      this.form.options.forEach((option, index) => {
        option.order = index;
      });
    },
    addOptionOther() {
      this.hasFreeOption = true;
    },
    deleteOptionOther() {
      this.hasFreeOption = false;
    },
    reset() {
      if (this.isSubmiting) return;
      this.resetForm();
      this.isCreateQuestion = false;
    },
    resetForm() {
      this.form = {
        typeQuestion: "",
        label: "",
        name: "",
        slug: "",
        helpText: "",
        helpFile: null,
        options: [],
        deletedOptionIds: [],
        errorMessage: "",
        conditions: "",
        bulkOption: "",
      };
      this.hasFreeOption = false;
      this.bulkOptionInsert = false;
      this.$v.form.$reset();
    },
    async createQuestionTemplate() {
      try {
        const newQuestionTemplate = await createQuestionTemplate({
          organizationId: this.$route.params.organizationId,
          payload: this.buildQuestion(),
        });
        this.questionTemplate.items.unshift(newQuestionTemplate);
        this.$emit("questionTemplateCreated");
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.fieldCreatedSuccessfully"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },

    // list template question
    async fetchQuestionTemplates() {
      this.questionTemplateFilter.loading = true;
      try {
        this.questionTemplateFilter.page = 1;
        this.questionTemplate = await fetchQuestionTemplates({
          organizationId: this.$route.params.organizationId,
          page: this.questionTemplateFilter.page,
          slug: this.questionTemplateFilter.slug,
          pageSize: this.questionTemplateFilter.pageSize,
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionTemplateFilter.loading = false;
    },
    filterQuestionTemplates() {
      if (this.questionTemplateFilter.searchTimeoutRef)
        clearTimeout(this.questionTemplateFilter.searchTimeoutRef);
      this.questionTemplateFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchQuestionTemplates();
      }, 200);
    },
    async loadMoreQuestionTemplates() {
      if (this.questionTemplateFilter.loading || !this.questionTemplate.next)
        return;
      this.questionTemplateFilter.page = this.questionTemplate.next;
      this.questionTemplateFilter.loading = true;
      try {
        const tmp = await fetchQuestionTemplates({
          organizationId: this.$route.params.organizationId,
          page: this.questionTemplateFilter.page,
          slug: this.questionTemplateFilter.slug,
          pageSize: this.questionTemplateFilter.pageSize,
        });
        this.questionTemplate = {
          ...tmp,
          items: [...this.questionTemplate.items, ...tmp.items],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionTemplateFilter.loading = false;
    },
    async handleQuestionTemplateScroll(event) {
      const el = event.target;
      const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;
      if (isAtBottom) {
        await this.loadMoreQuestionTemplates();
      }
    },
    isOptionDisabled(index, value) {
      return index == Object.keys(value).length - 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.question_container {
  display: flex;
  flex-direction: column;
  .creation__field {
    .field_content {
      max-height: 700px;
      display: flex;
      overflow: auto;
      overflow-x: hidden;
      padding-right: 6px;
      flex-direction: column;
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #fff;
        border-radius: 3px;
        border: 1px solid #eeeef7;
      }

      &::-webkit-scrollbar-thumb {
        background: #b6bdff;
        border-radius: 3px;
      }
      .label_field {
        font-size: 14px;
      }
    }
  }
  .input-fields {
    margin: 16px 0;
  }
  .template_section_select {
    min-height: 500px;
    max-height: 700px;
    overflow: auto;
    .items {
      padding: 0 8px;
    }
    .separator {
      margin: 16px 0;
      height: 1px;
      border-top: 1px solid black;
    }
    .questions__list {
      display: flex;
      flex-direction: column;
      gap: 16px;
      max-height: 360px;
      overflow-y: auto;
      padding: 0 8px;
      .item_skeleton {
        height: 56px;
        width: 100%;
        ::v-deep {
          .v-skeleton-loader__bone {
            height: 52px;
          }
        }
      }
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #fff;
        border-radius: 3px;
        border: 1px solid #eeeef7;
      }

      &::-webkit-scrollbar-thumb {
        background: #b6bdff;
        border-radius: 3px;
      }
      .questions__item {
        border: 1px solid black;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 6px;
        padding: 4px 8px;
        background: #f5f8ff;
        cursor: grabbing;

        .left_item {
          .question__name {
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 4px;
          }
          .question__slug {
            font-size: 12px;
          }
        }
        .right_item {
          font-size: 18px;
          font-weight: 500;
          color: #2536cc;
        }
      }
    }
  }
  .widget__actions {
    margin-top: auto;
    display: flex;
    justify-content: center;
    height: 50px;
    align-items: center;
  }
}
.help_section {
  display: flex;
  flex-direction: column;
  padding: 18px;
  gap: 8px;
  min-height: 255px;
  background: #fcfcfc;
  border: 1px solid #cfcfdc;
  border-radius: 5px;
  .help_title {
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #353542;
  }
}
.help_title_section {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 4px;
  .help_label {
    color: #2536cc;
  }
}
.option_container {
  padding: 0;
}
.option_item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
  .option_item_actions {
    display: flex;
    align-items: center;
    flex: none;
    width: 76px;
    margin: -26px 0 16px 0;
  }
}
.input_group {
  margin: 20px 0;
}
.separator {
  height: 1px;
  width: 98%;
  border: 1px solid #2536cc;
  margin: 0;
}
</style>
